import React from "react";
import { ShieldCheck, AlertCircle } from "lucide-react";
import { DISCLAIMER } from "../lib/api";
import { BRANDING } from "../lib/branding";

const Section = ({ title, children }) => (
  <section className="rounded-3xl bg-white border border-slate-100 p-7 shadow-soft">
    <h2 className="font-heading font-bold text-2xl text-slate-900">{title}</h2>
    <div className="mt-3 text-sm text-slate-600 leading-relaxed space-y-3">{children}</div>
  </section>
);

export default function Legal() {
  return (
    <div data-testid="legal-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#E1EFF6] text-[#1E4256] px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em]">
          <ShieldCheck className="w-3.5 h-3.5" /> Legal & Disclaimer
        </span>
        <h1 className="mt-4 font-heading font-bold text-4xl sm:text-5xl text-slate-900">Important information</h1>
        <p className="mt-4 text-slate-600 max-w-2xl mx-auto">{DISCLAIMER}</p>
      </div>

      <div className="mt-10 grid gap-5">
        <div className="rounded-3xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900">{BRANDING.name} is:</p>
            <ul className="mt-1.5 list-disc pl-5 space-y-1 text-sm text-amber-900/90">
              {BRANDING.notWhatItIs.map((line) => <li key={line}>{line}</li>)}
            </ul>
          </div>
        </div>

        <Section title="What we are">
          <p>{BRANDING.tagline} {BRANDING.positioning}</p>
        </Section>

        <Section title="What we never collect">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>OHIP numbers or any health card information.</li>
            <li>Medical records, prescriptions, diagnoses, treatments, or health history.</li>
            <li>Symptoms or any personal health information.</li>
            <li>Account creation — no signup or login is required.</li>
            <li>User profiles tied to your identity.</li>
          </ul>
        </Section>

        <Section title="What we do not do">
          <ul className="list-disc pl-5 space-y-1.5">
            <li>We do not provide medical advice or recommendations.</li>
            <li>We are not a healthcare authority and have no government partnership.</li>
            <li>We do not guarantee accuracy or real-time availability.</li>
            <li>We do not scrape Google, Yelp, or RateMDs reviews, or copy copyrighted content.</li>
            <li>We do not handle payments, bookings, or referrals.</li>
          </ul>
        </Section>

        <Section title="Community reports">
          <p>Reports are anonymous and intended to help other Ontario residents. URLs and personal health information are not allowed. Reports older than 30 days are weighted less; older than 60 days, very little.</p>
          <p>By submitting a report, you confirm: <span className="font-semibold text-slate-700">"{BRANDING.consentText}"</span></p>
        </Section>

        <Section title={`${BRANDING.demoLabel}`}>
  <p>The doctors shown in this MVP are <span className="font-semibold">fictional</span> sample/demo data created for demonstration purposes only. They do not represent real practitioners.</p>
</Section>

        <Section title="Confirm directly with the clinic">
          <p>Always call the clinic to confirm availability before relying on any signal shown here. Information may be outdated, incomplete, or based on community reports that have not been independently verified.</p>
        </Section>
      </div>
    </div>
  );
}
